<!--
  Muestra las páginas disponibles y permite navegar entre ellas fuera del marco HTML,
  para que el documento no altere la navegación. En la aplicación publicada, la lista ya
  llega filtrada por el servidor.

  El constructor puede crear páginas, reordenarlas y abrir sus ajustes. El espacio de
  esos controles se reserva para evitar movimientos al pasar el cursor.

  En la aplicación publicada quien mira elige dónde va la navegación: en una
  columna al lado del documento, o en una barra arriba con un menú que abre el
  cajón de las páginas (lo mismo que en un teléfono). lib/navLayout recuerda la
  elección por aplicación. El cajón flota sobre la página y se cierra al
  navegar, tocar fuera o pulsar Escape.
-->
<script lang="ts" module>
  import { pageIsLimited } from "@shared/pages";
  import { ADMIN_ROLE } from "@shared/people";
  import type { PageRecord } from "@shared/types";

  /**
   * A quien se le abrio una página, en una línea.
   *
   * No se lee con el ratón: es el nombre del sello para quien no lo ve. En
   * pantalla el icono se explica solo, y un globo aquí no cabe --a la derecha
   * tapa el botón de los ajustes, y arriba o abajo, las filas vecinas--.
   *
   * `admin` no entra en la cuenta: lo lleva toda página limitada --se lo pone
   * `withPageAdmin`-- así que nombrarlo no distingue una página de otra. Sin el
   * no queda nadie en la página que solo ve quien construye, y esa se cuenta
   * con palabras: el nombre `admin` no significaría nada para quien lo lea. La
   * página de todos no llega hasta aquí: no lleva sello.
   */
  export function accessLabel(page: PageRecord): string {
    const roles = (page.roles ?? []).filter((r) => r !== ADMIN_ROLE);
    if (!roles.length) return "Solo quien construye la aplicación";
    return `Pueden ver: ${roles.join(", ")}`;
  }

  export interface SidebarBuilder {
    /**
     * Ir al modo construcción: la conversación de la aplicación entera,
     * sin página delante. Sin esto --la IA no está conectada-- no se ofrece.
     */
    onBuildMode?: () => void;
    /** Se está en el modo construcción: el globo que lleva ahí no se muestra. */
    buildMode?: boolean;
    onCreate: () => void;
    /** Crea un separador: un texto que agrupa a las páginas de alrededor. */
    onCreateSeparator?: () => void;
    onOpenPageSettings: (page: PageRecord) => void;
    /** Los ids en el orden nuevo, después de arrastrar una página a otro sitio. */
    onReorder?: (ids: string[]) => void;
  }

  /** Clave del navegador donde se recuerda si el menú de esa aplicación está plegado. */
  const storageKey = (appId: string) => `plane_sidebar_${appId}`;

  /**
   * Plegado salvo que quien mira lo haya dejado abierto. Empezar plegado es lo
   * que hace que el documento se vea entero la primera vez.
   */
  /**
   * Por debajo del punto de corte `md` del sistema (48rem) no se fija: una
   * columna de 18rem al lado del documento lo dejaba, en un teléfono, en una
   * tira de un par de palabras. Ahí el sidebar es siempre el cajón flotante,
   * empieza plegado y no se ofrece cambiarlo a columna.
   */
  const NARROW_QUERY = "(max-width: 47.99rem)";

  /**
   * Desde aquí cabe la aplicación publicada entera: la columna (18rem), el
   * documento a su ancho de lectura (`.pantalla`, 76rem) y sus márgenes. Por
   * debajo, a quien visita la columna le quitaría sitio a la página: la
   * navegación pasa a la barra de arriba con el cajón, como en un teléfono.
   */
  const WIDE_QUERY = "(min-width: 97rem)";

  /**
   * Indica si el menú se dejó abierto la última vez. Sin almacenamiento disponible, queda abierto.
   */
  function remembered(appId: string): boolean {
    try {
      return localStorage.getItem(storageKey(appId)) !== "0";
    } catch {
      return true;
    }
  }
</script>

<script lang="ts">
  import { ROLE_ICON } from "@shared/people";
  import { type AppTheme, BUILD_MODE } from "@shared/types";
  import { type Snippet, untrack } from "svelte";
  import { cubicIn } from "svelte/easing";

  import { aiActivity } from "../../lib/aiActivity.svelte";
  import { cx } from "../../lib/cx";
  import type { NavLayout } from "../../lib/navLayout.svelte";
  import Icon from "../Icon.svelte";
  import { Button } from "../ui";
  import AppIcon from "./AppIcon.svelte";

  let {
    app,
    pages,
    activeId,
    onOpen,
    builder,
    headerActions,
    footer,
    account,
    pinned = false,
    cramped = false,
    layout = "sidebar",
    onToggleLayout,
  }: {
    app: { id: string; name: string; icon: string; theme?: AppTheme | null };
    pages: PageRecord[];
    activeId?: string;
    onOpen: (page: PageRecord) => void;
    /** Lo que solo se le ofrece a quien construye. Sin esto, es un visitante. */
    builder?: SidebarBuilder;
    /** Junto al nombre de la aplicación, arriba. Solo se ve sin `builder`. */
    headerActions?: Snippet;
    footer?: Snippet;
    /**
     * Quien entró, en la barra de arriba de un teléfono: en el cajón ya no
     * hay pie, solo las páginas.
     */
    account?: Snippet;
    /** Fijado: ocupa su columna al lado del documento en vez de taparlo. */
    pinned?: boolean;
    /**
     * El sitio que le dejan no da para columna y documento a la vez, aunque la
     * ventana sea ancha: se porta como en un teléfono. Es el caso del
     * constructor con la conversación al lado en una ventana mediana --o un
     * teléfono en modo escritorio--, donde fijado dejaba la página en una tira.
     */
    cramped?: boolean;
    /**
     * Dónde va la navegación de quien visita: en su columna, o en la barra de
     * arriba con el cajón, como en un teléfono. Solo cuenta sin `builder`.
     */
    layout?: NavLayout;
    /** Cambiar de una a otra. Sin esto no se ofrece el botón. */
    onToggleLayout?: () => void;
  } = $props();

  /** La ventana es de teléfono. */
  let narrowWindow = $state(matchMedia(NARROW_QUERY).matches);
  /** No hay sitio para una columna fija: solo el cajón. */
  const narrow = $derived(narrowWindow || cramped);
  /** La ventana da para la columna y la página publicada a su ancho. */
  let wideWindow = $state(matchMedia(WIDE_QUERY).matches);

  /**
   * La navegación es un cajón que se abre desde un botón: en un teléfono
   * siempre, y a quien visita también mientras la ventana no da para la
   * columna y la página a la vez.
   */
  const drawer = $derived(narrow || (!builder && !wideWindow));

  /**
   * Quien visita con la barra de arriba: el menú (si la navegación es un
   * cajón), la aplicación, claro u oscuro y quien entró; la navegación lleva
   * solo las páginas, debajo de la barra. Va con el cajón, o porque se pidió
   * arriba. Quien construye no la lleva: su encabezado ya es el del panel.
   */
  const topbar = $derived(!builder && (drawer || layout === "topbar"));

  /** En su columna de verdad: se pidió así y no toca el cajón. */
  const docked = $derived(pinned && !drawer);

  /** Quien pidió menos movimiento ve el cajón irse sin más. */
  const still = () => matchMedia("(prefers-reduced-motion: reduce)").matches;

  /*
   * El cajón entra con CSS (`@starting-style`), pero salir es quitarlo del
   * documento, y eso CSS no lo anima: se va deslizándose por donde vino. La
   * columna fija no se mueve: plegarla es cambiar de sitio, no un cajón.
   */
  function drawerOut(_node: Element) {
    if (!drawer || still()) return { duration: 0 };
    return { duration: 200, easing: cubicIn, css: (t: number) => `translate: ${(t - 1) * 100}% 0` };
  }

  /** El velo se apaga al mismo paso que se va el cajón. */
  function veilOut(_node: Element) {
    if (still()) return { duration: 0 };
    return { duration: 200, css: (t: number) => `opacity: ${t}` };
  }

  // `untrack` porque leer la aplicación aquí es a propósito: es el valor de
  // partida, y lo que pase después lo recoge el efecto de abajo. Con el cajón
  // se empieza plegado siempre: abierto taparía la página.
  let collapsed = $state(untrack(() => drawer || remembered(app.id)));

  $effect(() => {
    const narrowQuery = matchMedia(NARROW_QUERY);
    const wideQuery = matchMedia(WIDE_QUERY);
    const change = () => {
      narrowWindow = narrowQuery.matches;
      wideWindow = wideQuery.matches;
    };
    narrowQuery.addEventListener("change", change);
    wideQuery.addEventListener("change", change);
    return () => {
      narrowQuery.removeEventListener("change", change);
      wideQuery.removeEventListener("change", change);
    };
  });

  // Pasar a cajón (estrecharse, o pedir la barra de arriba) lo pliega; volver
  // a la columna trae lo que quien mira tenía guardado para esta aplicación.
  // Solo al cruzar: el valor de partida ya lo puso arriba.
  let wasDrawer = untrack(() => drawer);
  $effect(() => {
    const now = drawer;
    if (now === wasDrawer) return;
    wasDrawer = now;
    collapsed = now || remembered(untrack(() => app.id));
  });

  // Cambiar de aplicación trae la preferencia de la nueva, no la de la vieja.
  let lastApp = untrack(() => app.id);
  $effect(() => {
    if (app.id === lastApp) return;
    lastApp = app.id;
    collapsed = remembered(app.id);
  });

  // Plegado y desplegado es cosa de cada quien, y se recuerda por aplicación.
  // Lo que se hace con el cajón no cuenta: allí se pliega solo, y guardarlo
  // dejaría plegado el sidebar de escritorio sin que nadie lo pidiera.
  $effect(() => {
    if (drawer) return;
    try {
      localStorage.setItem(storageKey(app.id), collapsed ? "1" : "0");
    } catch {
      /* sin memoria del navegador, el sidebar simplemente vuelve a empezar plegado */
    }
  });

  /* ---------------------------------------------------------------- */
  /* Cambiar el orden arrastrando                                      */
  /* ---------------------------------------------------------------- */

  /**
   * Arrastrar una página a otro sitio de la lista.
   *
   * El orden es un campo de la página, así que hasta ahora había que abrir algo
   * para cambiarlo; aquí se hace donde se ve. `at` es el hueco donde caería
   * --el índice al que iría, contando los huecos y no las líneas--, y lo decide
   * de que mitad de la línea está el puntero.
   *
   * Los eventos se paran aquí a propósito: el editor entero es zona de soltar
   * archivos HTML, y arrastrar una página no es traer un archivo.
   */
  let draggingId = $state<string | null>(null);
  let at = $state<number | null>(null);

  const canDrag = $derived(!!builder?.onReorder && pages.length > 1);

  /**
   * El hueco donde se dibuja la línea, que no es siempre `at`: soltar en el
   * hueco de antes o el de después de la propia página la deja donde estaba.
   * Esos dos no se marcan, para que la línea signifique siempre lo mismo --se
   * va a mover ahí-- y no prometa un cambio que no va a pasar.
   */
  const dropAt = $derived.by(() => {
    if (draggingId === null || at === null) return null;
    const from = pages.findIndex((p) => p.id === draggingId);
    if (from < 0 || at === from || at === from + 1) return null;
    return at;
  });

  /** Termina el arrastre de una página y borra la marca de destino. */
  const endDrag = () => {
    draggingId = null;
    at = null;
  };

  /** Empieza a arrastrar una página para reordenarla. */
  const startDrag = (id: string, e: DragEvent) => {
    e.stopPropagation();
    if (!e.dataTransfer) return;
    e.dataTransfer.effectAllowed = "move";
    // Firefox no empieza a arrastrar si no viaja algo en el evento.
    e.dataTransfer.setData("text/plain", id);
    draggingId = id;
  };

  /**
   * Calcula, según la mitad de la fila sobre la que está el puntero, antes o después de qué página
   * caería lo arrastrado.
   */
  const overDrag = (index: number, e: DragEvent & { currentTarget: HTMLElement }) => {
    if (!draggingId) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
    const box = e.currentTarget.getBoundingClientRect();
    at = e.clientY < box.top + box.height / 2 ? index : index + 1;
  };

  /** Suelta la página arrastrada en su nuevo lugar. Si no cambia de posición, no hace nada. */
  const dropDrag = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const from = pages.findIndex((p) => p.id === draggingId);
    const to = at;
    endDrag();
    if (from < 0 || to === null || to === from || to === from + 1) return;
    const ids = pages.map((p) => p.id);
    const [moved] = ids.splice(from, 1);
    if (!moved) return;
    ids.splice(to > from ? to - 1 : to, 0, moved);
    builder?.onReorder?.(ids);
  };

  /**
   * Elegir página lo pliega mientras flota: está encima del documento y ya
   * cumplio su único trabajo. Fijado no se mueve, que para eso se fijo.
   */
  const open = (page: PageRecord) => {
    if (!docked) collapsed = true;
    onOpen(page);
  };

  /** Lo mismo con «Modo construcción», que no es una página. */
  const openBuildMode = () => {
    if (!docked) collapsed = true;
    builder?.onBuildMode?.();
  };

  /* ---------------------------------------------------------------- */
  /* Flotante: se comporta como un menú                                */
  /* ---------------------------------------------------------------- */

  /**
   * Abierto encima del documento, se cierra en cuanto se atiende a otra cosa:
   * tocar fuera, Escape, o llevarse el foco al marco de la página --que es un
   * documento aparte y no manda su click aquí, así que se lee por el foco--.
   *
   * Solo escucha mientras flota y está abierto: fijado no se cierra por tocar
   * el documento, que para eso se fijo.
   */
  let box = $state<HTMLElement | null>(null);

  $effect(() => {
    if (docked || collapsed || !box) return;
    const el = box;

    const outside = (e: PointerEvent) => {
      const target = e.target;
      if (target instanceof Node && el.contains(target)) return;
      // El botón de la barra lo cierra él mismo: cerrarlo aquí haría que su
      // click lo volviera a abrir.
      if (target instanceof Element && target.closest(".btn-open-nav-mobile")) return;
      collapsed = true;
    };

    const escape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      collapsed = true;
    };

    // El foco se fue al marco de la página: por dentro el click no se ve.
    const away = () => {
      if (document.activeElement?.tagName === "IFRAME") collapsed = true;
    };

    // En captura, para que nada de lo de abajo se lo trague antes.
    document.addEventListener("pointerdown", outside, true);
    document.addEventListener("keydown", escape, true);
    window.addEventListener("blur", away);
    return () => {
      document.removeEventListener("pointerdown", outside, true);
      document.removeEventListener("keydown", escape, true);
      window.removeEventListener("blur", away);
    };
  });
</script>

<!--
  Cambiar dónde va la navegación: de la columna a la barra de arriba, o al
  revés. Solo con sitio para la columna: con el cajón no hay elección.
-->
{#snippet layoutToggle()}
  {#if onToggleLayout && !drawer}
    {@const label = layout === "topbar" ? "Navegación a un lado" : "Navegación arriba"}
    <Button
      tip={label}
      aria-label={label}
      size="sm"
      onclick={onToggleLayout}
      tipSide="bottom"
      buttonClass="btn-toggle-nav-layout"
      class="btn-ghost btn-icon"
    >
      <Icon name={layout === "topbar" ? "panel-right-open" : "panel-top-close"} size={16} />
    </Button>
  {/if}
{/snippet}

<!-- La línea que dice donde caería la página que se está arrastrando. -->
{#snippet dropLine()}
  <span aria-hidden="true" class="drop-line"></span>
{/snippet}

<!--
  El icono con el que se resume quien abre una página.

  Solo se dibuja la excepción --tener roles marcados--, y con el icono con el
  que los roles se reconocen en todas partes. La página que abre cualquiera es
  la norma y no lleva sello: en una lista, un icono repetido en todas las
  líneas no dice nada y le quita fuerza al que si.

  Sin globo: el sello se retira al pasar el ratón --le deja el hueco al botón
  de los ajustes-- así que ir a señalarlo es justo lo que lo hace desaparecer,
  y colgarlo de la fila entera tapa lo de al lado. Los roles se leen donde se
  marcan, en los ajustes de la página.
-->
{#snippet accessMark(page: PageRecord)}
  {#if pageIsLimited(page)}
    <span role="img" aria-label={accessLabel(page)}>
      <Icon name={ROLE_ICON} size={18} />
    </span>
  {/if}
{/snippet}

{#if topbar}
  <header id="app-topbar-mobile" class="topbar-app-mobile">
    {#if drawer}
      <Button
        aria-label={collapsed ? "Abrir la navegación" : "Cerrar la navegación"}
        aria-expanded={!collapsed}
        size="sm"
        onclick={() => (collapsed = !collapsed)}
        buttonClass="btn-open-nav-mobile"
        class="btn-ghost btn-icon"
      >
        <!-- El icono gira al cambiar de menú a cerrar, y al revés. -->
        {#key collapsed}
          <span class="icon-nav-toggle">
            <Icon name={collapsed ? "menu-01" : "cancel-01"} size={20} />
          </span>
        {/key}
      </Button>
    {/if}
    <AppIcon {app} />
    <span class="app-name">{app.name}</span>
    {#if headerActions || onToggleLayout}
      <div class="topbar-app-mobile-actions">
        {@render headerActions?.()}
        {@render layoutToggle()}
      </div>
    {/if}
    {#if account}
      <div class="topbar-app-mobile-user">{@render account()}</div>
    {/if}
  </header>
{/if}

{#if collapsed && !docked}
  {#if !topbar}
    <div id="app-sidebar-collapsed" class="sidebar-app-collapsed flex flex-col items-center gap-2">
      <Button
        tip="Desplegar la navegación"
        aria-label="Desplegar la navegación"
        size="sm"
        onclick={() => {
          collapsed = false;
        }}
        buttonClass="btn-expand-sidebar"
        class="btn-icon btn-expand-sidebar-look"
      >
        <Icon name="panel-left" size={16} />
      </Button>
    </div>
  {/if}
{:else}
  <!-- En un teléfono el cajón tapa el documento: detrás, un velo oscuro y
       desenfocado. Tocarlo cierra el cajón, como cualquier toque fuera. -->
  {#if drawer && !docked}
    <div
      aria-hidden="true"
      class={cx("backdrop-sidebar plane-backdrop", topbar && "below-topbar")}
      out:veilOut|global
    ></div>
  {/if}
  <aside
    id="app-sidebar"
    bind:this={box}
    out:drawerOut
    class={cx(
      "sidebar-app flex flex-col",
      docked ? "pinned" : "floating",
      drawer && "is-drawer",
      topbar && "below-topbar",
    )}
  >
    <!-- Quien visita la aplicación publicada no tiene encabezado arriba: aquí
         es donde se entera de en que aplicación está. -->
    {#if !builder && !topbar}
      <div class="sidebar-head">
        <AppIcon {app} />
        <span class="app-name">{app.name}</span>
        {#if headerActions || onToggleLayout}
          <div class="sidebar-head-actions">
            {@render headerActions?.()}
            <!-- Va al final de la fila, a la derecha de claro u oscuro: es lo
                 último que se toca, y en la barra de arriba cae en el mismo
                 sitio. -->
            {@render layoutToggle()}
          </div>
        {/if}
      </div>
    {/if}

    <nav aria-label="Páginas de la aplicación" class="nav-pages">
      <!-- La lista se explica sola: el nombre de cada página y los separadores
           que las agrupan dicen más que un título encima. -->

      <ul id="app-sidebar-pages" class="list-pages">
        {#each pages as page, index (page.id)}
          {#if page.separator}
            <!-- No es una página: solo un texto que agrupa a las de alrededor.
                 Se arrastra igual que cualquier fila, pero no se abre ni marca
                 "activa". -->
            <li
              class={cx(
                "sidebar-page-row",
                "sidebar-separator-row",
                draggingId === page.id && "sidebar-page-dragging",
              )}
              draggable={canDrag}
              ondragstart={canDrag ? (e) => startDrag(page.id, e) : undefined}
              ondragover={canDrag ? (e) => overDrag(index, e) : undefined}
              ondrop={canDrag ? dropDrag : undefined}
              ondragend={canDrag ? endDrag : undefined}
            >
              {#if dropAt === index}
                {@render dropLine()}
              {/if}

              <span class="sidebar-separator-label eyebrow">{page.name}</span>

              <!-- El mismo sello que en una página: dice que el grupo está
                   limitado a unos roles, y se corre a la izquierda al pasar el
                   ratón para dejarle el hueco al botón de los ajustes. -->
              {#if builder}
                <div class="btn-right">{@render accessMark(page)}</div>
                <div class="sidebar-page-settings-slot">
                  <Button
                    aria-label={`Ajustes del separador "${page.name}"`}
                    onclick={() => builder.onOpenPageSettings(page)}
                    class="btn btn-ghost btn-icon btn-page-settings btn-settings-separator"
                  >
                    <Icon name="more-vertical" size={20} />
                  </Button>
                </div>
              {/if}
            </li>
          {:else}
            {@const active = page.id === activeId}
            <li
              class={cx(
                "sidebar-page-row",
                active ? "sidebar-page-active" : "sidebar-page-idle",
                draggingId === page.id && "sidebar-page-dragging",
              )}
              draggable={canDrag}
              ondragstart={canDrag ? (e) => startDrag(page.id, e) : undefined}
              ondragover={canDrag ? (e) => overDrag(index, e) : undefined}
              ondrop={canDrag ? dropDrag : undefined}
              ondragend={canDrag ? endDrag : undefined}
            >
              <!-- Donde caería la página que se arrastra. -->
              {#if dropAt === index}
                {@render dropLine()}
              {/if}

              <button
                type="button"
                onclick={() => open(page)}
                aria-current={active ? "page" : undefined}
                class="btn btn-ghost btn-open-page sidebar-page-btn"
              >
                <Icon name={page.icon} size={20} class="sidebar-page-icon" />
                <span class="sidebar-page-name">{page.name}</span>

                <div class="btn-right">
                  <!-- La IA está trabajando en esta página. Es la única señal que
                     queda desde otra página: la fila dice en cual trabaja, y
                     abrirla es el clic que la fila ya hacía. Va fuera de los
                     sellos permanentes porque esos se retiran al pasar el
                     ratón, y esta no puede apagarse justo al ir a pulsarla. -->
                  {#if builder && aiActivity.has(page.id)}
                    <span
                      role="img"
                      data-tip="Se está trabajando aquí"
                      data-tip-side="right"
                      aria-label="Se está trabajando aquí"
                      class="sidebar-page-ai"
                    >
                      <Icon name="ai-magic" size={18} />
                    </span>
                  {/if}

                  {#if page.isHome}
                    <span role="img" aria-label="Página de inicio">
                      <Icon name="home-11" size={18} />
                    </span>
                  {/if}
                  {#if builder}
                    {@render accessMark(page)}
                  {/if}
                </div>
              </button>

              {#if builder}
                <div class="sidebar-page-settings-slot">
                  <Button
                    aria-label={`Ajustes de "${page.name}"`}
                    onclick={() => builder.onOpenPageSettings(page)}
                    class="btn btn-ghost btn-icon btn-page-settings"
                  >
                    <Icon name="more-vertical" size={20} />
                  </Button>
                </div>
              {/if}
            </li>
          {/if}
        {/each}

        <!-- La última posición: soltar debajo de todo manda la página al final. -->
        {#if dropAt === pages.length}
          <li aria-hidden="true" class="drop-target-end">{@render dropLine()}</li>
        {/if}

        {#if pages.length === 0}
          <li class="sidebar-empty">
            {builder ? "Todavía no hay ninguna página." : "No hay páginas que puedas abrir."}
          </li>
        {/if}

        <!-- Crear una página es el siguiente elemento de la lista, no un botón
             aparte: se pide donde terminan las que ya hay. El separador va al
             lado, más chico: se crea mucho menos seguido. -->
        {#if builder}
          <li class="sidebar-new-row flex items-center gap-1">
            <button
              type="button"
              onclick={builder.onCreate}
              class="btn btn-ghost btn-new-page sidebar-new-page-btn"
            >
              <Icon name="file-add" size={18} class="sidebar-new-page-icon" />
              <span class="sidebar-new-page-label">Nueva página</span>
            </button>
            {#if builder.onCreateSeparator}
              <Button
                tip="Nuevo separador"
                aria-label="Nuevo separador"
                size="sm"
                onclick={builder.onCreateSeparator}
                tipSide="top"
                class="btn btn-ghost btn-icon btn-new-separator"
              >
                <Icon name="separator-horizontal" size={14} />
              </Button>
            {/if}
          </li>
        {/if}
      </ul>

      <!-- «Modo construcción» no es una página sino el sitio desde donde
           la IA crea varias: flota en la esquina, con forma de globo de
           conversación, en vez de ocupar una fila de la lista. Estando ya en
           «Modo construcción» sobra, y gira un anillo mientras la IA construye. -->
      {#if builder?.onBuildMode && !builder.buildMode}
        {@const busy = aiActivity.askedFrom(BUILD_MODE)}
        <Button
          tip={busy ? "Modo construcción · se está construyendo" : "Modo construcción"}
          tipSide="top"
          aria-label="Modo construcción"
          onclick={openBuildMode}
          buttonClass="btn-open-build-mode"
          class={cx("btn-build-mode-bubble mark-build-mode", busy && "btn-build-mode-bubble-busy")}
        >
          <Icon name="ai-elements" size={20} />
        </Button>
      {/if}
    </nav>

    {#if footer && !topbar}
      <div class="sidebar-foot">
        {@render footer()}
      </div>
    {/if}
  </aside>
{/if}

<style>
  /* ------------------------------------------------ */
  /* Plegado: el botoncito que devuelve la navegación  */
  /* ------------------------------------------------ */

  /* Absoluto dentro de la escena, no fijo en la ventana: así cae al borde del
     documento y nunca sobre lo que haya a su izquierda, como el dock. La tira
     baja hasta el fondo y el botón vive arriba, donde siempre se lo busco, a
     la misma distancia del borde de arriba que del de la izquierda. En lo
     publicado la página le deja libre esa franja (`AppView`). */
  .sidebar-app-collapsed {
    position: absolute;
    top: var(--sp-8);
    bottom: 0;
    left: var(--sp-8);
    z-index: 10;

    & :global(.btn-expand-sidebar-look) {
      border-radius: 62.5rem;
      box-shadow: var(--shadow-sm);
    }
  }

  /* ------------------------------------------------ */
  /* La barra de quien visita con el cajón              */
  /* ------------------------------------------------ */

  /*
   * Encima de la escena, y el documento empieza debajo de ella (`AppView`):
   * no tapa nada de la página. Debajo del velo y del cajón.
   */
  .topbar-app-mobile {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    z-index: 8;
    display: flex;
    height: 3rem;
    align-items: center;
    gap: var(--sp-8);
    /* El mismo margen que los rótulos de la navegación (lista + rótulo): el
       icono de la aplicación cae en la línea de los grupos de abajo, y la
       derecha lleva el mismo aire para que la barra quede pareja. */
    padding: 0 calc(var(--sp-14) + var(--sp-10));

    /* En un teléfono el ancho manda: la barra vuelve a su margen corto. */
    @media (max-width: 47.99rem) {
      padding: 0 var(--sp-8);
    }
    border-bottom: var(--border-width) solid color-mix(in oklab, var(--border) 60%, transparent);
    background: var(--bg-sidebar);

    & .app-name {
      min-width: 0;
      flex: 1 1 0%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-weight: 600;
      font-size: var(--text-sm);
      line-height: var(--text-sm--line-height);
    }

    /* El icono del menú entra girando: de las rayas a la cruz y de vuelta. */
    & .icon-nav-toggle {
      display: grid;
      animation: topbar-nav-icon 240ms cubic-bezier(0.2, 0.8, 0.2, 1);

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    }

    & .topbar-app-mobile-actions,
    & .topbar-app-mobile-user {
      display: flex;
      align-items: center;
      gap: var(--sp-4);
    }
  }

  @keyframes topbar-nav-icon {
    from {
      opacity: 0;
      rotate: -90deg;
      scale: 0.6;
    }
  }

  /* El velo de `.plane-backdrop` (entra solo, ver `animations.css`), justo
     debajo del cajón. Dentro de la escena y no de la ventana: con la barra
     arriba empieza debajo de ella, que sigue a la vista y se puede tocar. */
  .backdrop-sidebar {
    position: absolute;
    z-index: 9;

    &.below-topbar {
      top: 3rem;
    }
  }

  /* ------------------------------------------------ */
  /* El sidebar abierto                                 */
  /* ------------------------------------------------ */

  .sidebar-app {
    /* El mismo ancho de las dos maneras: fijarlo o soltarlo cambia donde se
       dibuja, no cuanto mide, así que la lista no se recompone al hacerlo. */
    --sidebar-width: 18rem;

    background: var(--bg-sidebar);
    width: var(--sidebar-width);

    /* El borde cae del lado por el que se toca el documento: el sidebar vive a
       la izquierda, así que el borde va a su derecha. */
    border-right: var(--border-width) solid var(--border);

    &.pinned {
      position: relative;
      /* flex-shrink: 0 — columna de ancho fijo dentro de una fila. */
      flex-shrink: 0;
      z-index: 10;
    }

    &.floating {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      z-index: 10;
      box-shadow: var(--shadow-sm);
    }

    /* El cajón se levanta como lo que flota --modal, cajón-- y en un teléfono
       deja siempre una franja del documento a la vista, que es donde se toca
       para cerrarlo. */
    /* Con la barra arriba, la navegación empieza debajo de ella: en columna
       y como cajón. La barra es de 3rem, la misma franja que el documento deja
       libre (`AppView`). */
    &.below-topbar {
      &.pinned {
        margin-top: 3rem;
      }

      &.floating {
        top: 3rem;
      }

      /* Su sombra no sube a la barra: se recorta por arriba y sigue a los
         lados y abajo. Sin esto oscurecía la barra encima del cajón. */
      &.is-drawer {
        clip-path: inset(0 -3rem -3rem 0);
      }
    }

    &.is-drawer {
      max-width: calc(100vw - 3rem);
      box-shadow: var(--shadow-lg);
      transition: translate 240ms cubic-bezier(0.2, 0.8, 0.2, 1);

      /* Entra deslizándose desde el borde, como un cajón. */
      @starting-style {
        translate: -100% 0;
      }

      @media (prefers-reduced-motion: reduce) {
        transition: none;
      }
    }

    /* Quien visita no tiene encabezado arriba: se le presenta la aplicación. */
    & .sidebar-head {
      display: flex;
      align-items: center;
      gap: var(--sp-8);
      padding: var(--sp-8) var(--sp-14);
      border-bottom: var(--border-width) solid var(--border);

      & .app-name {
        min-width: 0;
        flex: 1 1 0%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-weight: 600;
        font-size: var(--text-sm);
        line-height: var(--text-sm--line-height);
      }

      & .sidebar-head-actions {
        display: flex;
        align-items: center;
        gap: var(--sp-4);
      }
    }

    /* Con la cabecera de quien visita encima, la lista se separa de su raya. */
    & .sidebar-head + .nav-pages .list-pages {
      padding-top: var(--sp-10);
    }

    & .nav-pages {
      --build-mode-size: 2.5rem;
      --build-mode-gap: var(--sp-12);

      position: relative;
      display: flex;
      flex-direction: column;
      flex: 1 1 0%;
      min-height: 0;

      /* El globo del modo construcción flota en la esquina de abajo. La lista deja
         ese alto libre al final para que la última página no quede debajo. */
      &:has(:global(.btn-build-mode-bubble)) .list-pages {
        padding-bottom: calc(var(--build-mode-size) + 2 * var(--build-mode-gap));
      }

      /* El globo es la marca del modo construcción (`.mark-build-mode`, en
         global.css); aquí solo se coloca y se mueve al tocarlo. */
      & :global(.btn-build-mode-bubble) {
        position: absolute;
        right: var(--build-mode-gap);
        bottom: var(--build-mode-gap);
        z-index: 1;
        --mark-size: var(--build-mode-size);
        transition:
          transform 160ms cubic-bezier(0.2, 0.8, 0.2, 1),
          background-color 150ms;

        &:hover {
          background: color-mix(in oklab, var(--mark-build-mode-bg), black 12%);
          transform: translateY(-1px);
        }

        &:active {
          transform: scale(0.96);
        }

        /* Construyendo: un anillo gira alrededor del globo. */
        &.btn-build-mode-bubble-busy::after {
          content: "";
          position: absolute;
          inset: -0.25rem;
          border-radius: inherit;
          border: 2px solid transparent;
          border-top-color: var(--mark-build-mode-bg);
          animation: sidebar-build-mode-spin 1s linear infinite;
        }
      }

      & .list-pages {
        display: flex;
        min-height: 0;
        flex: 1 1 0%;
        flex-direction: column;
        gap: 0.125rem;
        overflow-y: auto;
        padding: 0 var(--sp-14) var(--sp-8);

        & .sidebar-page-row {
          --page-icon-size: 1.25rem;
          --page-seal-size: 1.125rem;
          --page-settings-size: 1.75rem;
          /* El margen de los dos lados: el icono de la izquierda y los sellos
             o el botón de la derecha quedan a la misma distancia del borde. */
          --page-edge: var(--sp-14);
          /* El lavado de la fila. Los sellos lo repiten sobre el fondo del
             sidebar para tapar opacos el final del nombre cuando se corren. */
          --page-wash: transparent;

          display: flex;
          position: relative;
          align-items: center;
          gap: var(--sp-4);
          color: var(--text-secondary);
          border-radius: var(--radius-md);
          transition:
            background-color 150ms,
            color 150ms;

          /* Un tinte del acento, no el acento entero: el nombre en negrita
             acompaña al color y con eso se sabe cual está abierta. */
          &.sidebar-page-active {
            --page-wash: var(--accent-soft);

            .sidebar-page-btn {
              color: var(--accent-soft-text);
            }
          }

          /* Por la fila y no por el botón: el de los ajustes cae encima de la
             píldora, y pasar a él no tiene que apagarla. */
          &.sidebar-page-idle:is(:hover, :focus-within) {
            --page-wash: oklch(from var(--text-primary) l c h / 6%);

            .sidebar-page-btn {
              color: var(--text-primary);
            }
          }

          &.sidebar-page-dragging {
            opacity: 0.4;
          }

          /* La señal de la IA: lo único de la fila que cambia solo, así que
               late en vez de quedarse quieta. */
          & .sidebar-page-ai {
            display: flex;
            flex: none;
            align-items: center;
            justify-content: center;
            width: var(--page-seal-size);
            height: var(--page-seal-size);
            line-height: 0;
            color: var(--accent);
            animation: sidebar-ai-pulse 1.6s ease-in-out infinite;
          }

          /* Separador */
          /* Más aire arriba que abajo: el rótulo encabeza las páginas que
             vienen detrás, no se queda a medio camino entre dos grupos. */
          &.sidebar-separator-row {
            min-height: 2rem;
            margin-top: var(--sp-12);

            /* Separador de texto */
            /* El rótulo es `.eyebrow` del catálogo; aquí solo su sitio en la
               fila y el recorte, que el nombre lo escribe quien construye. */
            & .sidebar-separator-label {
              margin: 0;
              padding-left: var(--sp-10);
              font-size: calc(var(--text-xs) * 0.9);
              min-width: 0;
              flex: 1 1 0%;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }

            /* Sin píldora, el borde de la derecha es el de la lista. */
            & .btn-right {
              margin-right: var(--page-edge);
            }
          }

          /* Botón de cada página*/
          & .sidebar-page-btn {
            width: 100%;
            text-align: left;
            color: inherit;
            font-weight: 500;
            padding-right: var(--page-edge);
            background: var(--page-wash);
            transition:
              background-color 150ms,
              color 150ms;

            /* Icono de la página */
            & :global(.sidebar-page-icon) {
              display: flex;
              flex: none;
              align-items: center;
              justify-content: center;
              width: var(--page-icon-size);
              height: var(--page-icon-size);
              line-height: 0;
              color: inherit;
              transition: color 150ms;
            }

            /* Nombre de la página */
            & .sidebar-page-name {
              flex: 1 1 auto;
              min-width: 0;
              white-space: normal;
              overflow-wrap: anywhere;
            }
          }

          /*
            Los sellos de la derecha (inicio, roles). Siempre ocupan al menos
            el ancho de uno, aunque la fila no lleve ninguno: ese hueco es el
            que toma el botón de los ajustes, así que el nombre no cambia de
            ancho al pasar el ratón.

            Al pasar, el botón entra por la derecha y empuja los sellos hacia
            la izquierda. Van por encima del final del nombre, con su propio
            fondo opaco --el del sidebar y encima el lavado de la fila-- y un
            degradado delante para que el texto se desvanezca en vez de
            cortarse en seco.
          */
          & .btn-right {
            position: relative;
            display: inline-flex;
            flex: none;
            justify-content: flex-end;
            gap: var(--sp-4);
            min-width: var(--page-seal-size);
            background: linear-gradient(var(--page-wash), var(--page-wash)), var(--bg-sidebar);
            transition: transform 240ms cubic-bezier(0.2, 0.8, 0.2, 1);

            & span {
              display: inline-flex;
            }

            /* Sin sellos no tapa nada. Con `:has` y no con `:empty`: entre
               los `{#if}` Svelte deja nodos de texto vacíos, y con ellos la
               caja nunca contaba como vacía. */
            &:not(:has(> *)) {
              background: none;

              &::before {
                display: none;
              }
            }

            &::before {
              content: "";
              position: absolute;
              inset-block: calc(-1 * var(--sp-6));
              right: 100%;
              width: var(--sp-18);
              background: inherit;
              mask-image: linear-gradient(to right, transparent, #000);
              opacity: 0;
              transition: opacity 240ms;
              pointer-events: none;
            }
          }

          /*
            El botón de los ajustes vive fuera de la píldora y cae sobre su
            final: el glifo termina donde terminan los sellos. La ranura recorta
            el botón mientras entra desde fuera; `overflow: clip` con margen deja
            ver entero el anillo del foco.
          */
          & .sidebar-page-settings-slot {
            position: absolute;
            top: 0;
            right: calc(var(--page-edge) - (var(--page-settings-size) - var(--page-icon-size)) / 2);
            bottom: 0;
            display: flex;
            align-items: center;
            overflow: clip;
            overflow-clip-margin: var(--sp-4);
            pointer-events: none;

            & :global(.btn-page-settings) {
              width: var(--page-settings-size);
              height: var(--page-settings-size);
              min-height: 0;
              opacity: 0;
              transform: translateX(calc(var(--page-settings-size) + var(--page-edge)));
              transition:
                transform 240ms cubic-bezier(0.2, 0.8, 0.2, 1),
                opacity 240ms;
            }
          }

          &:is(:hover, :focus-within) {
            /* Los sellos solo se apartan para dejar sitio al botón de los
               ajustes; en la aplicación publicada no hay botón y se quedan. */
            &:has(.sidebar-page-settings-slot) .btn-right:has(> *) {
              transform: translateX(calc(-1 * (var(--page-seal-size) + var(--sp-10))));

              &::before {
                opacity: 1;
              }
            }

            & .sidebar-page-settings-slot {
              pointer-events: auto;

              & :global(.btn-page-settings) {
                opacity: 1;
                transform: none;
              }
            }
          }

          /* La guía de bienvenida señala este botón en uno de sus pasos, y ahí
             tiene que verse sin el puntero encima. La marca la pone el propio
             recorrido en la raíz del documento; ver `guide/GuideTour.svelte`.
             Los separadores se quedan fuera: el paso habla de una página. */
          :global(html[data-guide-reveal="page-settings"]) &:not(.sidebar-separator-row) {
            & .sidebar-page-settings-slot {
              pointer-events: auto;
            }

            & :global(.btn-page-settings) {
              opacity: 1;
              transform: none;
            }
          }
        }

        /* El hueco donde caería la página arrastrada.

           Absoluta sobre la fila --que es `position: relative`-- y no un hijo
           más: dentro del flujo la fila es una caja flex y la línea se quedaba
           sin ancho, o sea invisible. Así cruza la fila entera y se dibuja
           justo en el hueco de arriba, que es el sitio al que iría. */
        & .drop-line {
          pointer-events: none;
          position: absolute;
          top: -0.125rem;
          right: 0;
          left: 0;
          z-index: 1;
          height: 0.125rem;
          border-radius: 62.5rem;
          background: var(--accent);
        }

        /* La última posición no ocupa alto: solo sostiene a la línea. */
        & .drop-target-end {
          position: relative;
          height: 0;

          & .drop-line {
            top: 0;
          }
        }

        & .sidebar-empty {
          padding: 0 var(--sp-8) var(--sp-24);
          text-align: center;
          font-size: var(--text-xs);
          line-height: var(--text-xs--line-height);
          color: var(--text-muted);
        }

        & .sidebar-new-row {
          width: 100%;
        }

        & .sidebar-new-page-btn {
          flex: 1 1 0%;
          gap: var(--sp-11);
          text-align: left;
          font-weight: 500;
          color: var(--text-muted);

          &:hover {
            color: var(--text-primary);
          }

          &:focus-visible {
            outline: 2px solid var(--accent);
            outline-offset: 1px;
          }

          & .sidebar-new-page-label {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
      }
    }

    & .sidebar-foot {
      display: flex;
      align-items: center;
      gap: var(--sp-8);
      border-top: var(--border-width) solid var(--border);
      padding: var(--sp-10) var(--sp-14);
    }
  }

  /* Quien prefiere que nada se mueva no recibe el latido: el color basta. */
  @keyframes sidebar-ai-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.35;
    }
  }

  @keyframes sidebar-build-mode-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    :global(.btn-build-mode-bubble)::after {
      animation: none;
    }

    .sidebar-page-ai {
      animation: none;
    }
  }
</style>
